/**
 * Renders cart-aware Voyado recommendations using the theme's Shopify cards.
 * Shopify remains authoritative for cart contents, pricing and inventory.
 */
(function initializeVoyadoCartPage(window, document) {
    'use strict';

    if (
        !window.VOYADO_CONTEXT?.enabled ||
        !window.VOYADO_ELEVATE?.createClient ||
        !window.VOYADO_PRODUCT_CARD_LOADER?.load ||
        customElements.get('voyado-cart-page')
    ) {
        return;
    }

    const cardLoader = window.VOYADO_PRODUCT_CARD_LOADER;
    const PRODUCT_CONTEXT_ALGORITHMS = new Set([
        'ALTERNATIVES',
        'STYLE_WITH',
        'UPSELL',
        'MORE_FROM_SERIES',
        'ADD_TO_CART_RECS',
    ]);

    class VoyadoCartPage extends HTMLElement {
        constructor() {
            super();
            this.client = window.VOYADO_ELEVATE.createClient();
            this.requestController = null;
            this.load();
        }

        disconnectedCallback() {
            this.requestController?.abort();
        }

        getCartKeys() {
            try {
                const value = JSON.parse(
                    this.querySelector('[data-voyado-cart-keys]')?.textContent || '[]'
                );

                return Array.isArray(value)
                    ? [...new Set(value.map((key) => String(key).trim()).filter(Boolean))]
                    : [];
            } catch {
                return [];
            }
        }

        get limit() {
            return Math.max(1, Number(this.dataset.limit) || 6);
        }

        get algorithm() {
            return String(this.dataset.algorithm || 'CART').toUpperCase();
        }

        get display() {
            return this.dataset.display === 'grid' ? 'grid' : 'slider';
        }

        get showAddToCart() {
            return this.dataset.showAddToCart !== 'false';
        }

        getRecommendationList() {
            const recommendationList = {
                id: 'CART-1',
                algorithm: this.algorithm,
                label: this.dataset.label || '',
                limit: this.limit,
                visualization: this.display === 'grid' ? 'GRID' : 'CAROUSEL',
            };

            if (PRODUCT_CONTEXT_ALGORITHMS.has(this.algorithm)) {
                const productKey = String(this.dataset.productId || '').trim();
                if (!productKey) {
                    throw new Error(`${this.algorithm} requires a product key.`);
                }

                recommendationList.params = { productKey };
            }

            return recommendationList;
        }

        async load() {
            const cartKeys = this.getCartKeys();
            if (!cartKeys.length) {
                this.hidden = true;
                return;
            }

            this.requestController?.abort();
            this.requestController = new AbortController();

            try {
                const response = await this.client.cartPage(
                    { cart: cartKeys },
                    {
                        cart: { include: false },
                        recommendationLists: [this.getRecommendationList()],
                    },
                    { signal: this.requestController.signal }
                );

                const products = (response?.recommendationLists || [])
                    .filter((list) => list?.visible !== false)
                    .flatMap((list) => list?.productGroups || [])
                    .map((group) => group?.products?.[0])
                    .filter(Boolean)
                    .slice(0, this.limit);
                const cards = (await Promise.all(
                    products.map((product) => cardLoader.load(product, {
                        showAddToCart: this.showAddToCart,
                    }))
                )).filter(Boolean);

                if (!cards.length) throw new Error('No Voyado cart recommendations found.');
                this.render(cards, 'voyado');
            } catch (error) {
                if (this.requestController.signal.aborted) return;

                window.VOYADO_ELEVATE.debug?.('Cart page fallback', {
                    message: error?.message || String(error),
                });
                await this.loadShopifyFallback();
            }
        }

        async loadShopifyFallback() {
            try {
                const productId = String(this.dataset.productId || '').trim();
                if (!productId) throw new Error('A fallback product ID is required.');

                const url = new URL(
                    `${window.location.origin}${window.routes.product_recommendations_url}.json`
                );
                url.searchParams.set('product_id', productId);
                url.searchParams.set('limit', String(this.limit));

                const response = await fetch(url, { credentials: 'same-origin' });
                if (!response.ok) {
                    throw new Error(`Fallback request failed: ${response.status}`);
                }

                const result = await response.json();
                const cards = (await Promise.all(
                    (result.products || []).map((product) => (
                        cardLoader.load(product, {
                            tracked: false,
                            showAddToCart: this.showAddToCart,
                        })
                    ))
                )).filter(Boolean);

                if (!cards.length) throw new Error('No Shopify cart recommendations found.');
                this.render(cards, 'shopify');
            } catch (error) {
                this.hidden = true;
                console.warn('[Voyado cart page] Recommendations failed', error);
            }
        }

        render(cards, source) {
            const container = this.querySelector('[data-products-ajax]');
            if (!container) return;

            container.replaceChildren(...cards);
            container.removeAttribute('aria-busy');
            this.querySelector('gridy-slider')?.refresh();
            this.dataset.recommendationSource = source;
            this.hidden = false;
        }
    }

    customElements.define('voyado-cart-page', VoyadoCartPage);
})(window, document);
